import React from 'react';
import { CTA_PREFIXES, trackCtaClick } from '../../hooks/useClickTracking';
import { Image } from '../Image/Image';
import { FooterLink, SocialLink } from './types';

export interface BottomBarProps {
  /** URL for the footer logo image */
  logoImageUrl?: string;
  /** Alt text for the footer logo */
  logoAlt?: string;
  /** URL the logo links to */
  logoUrl?: string;
  /** CTA tracking identifier for the logo link */
  logoCtaId?: string;
  /** Link Decorator appendix for the logo */
  logoLdAppendix?: string;
  /** Copyright notice text */
  copyrightText?: string;
  /** First row of footer links */
  row1Links: FooterLink[];
  /** Second row of footer links */
  row2Links: FooterLink[];
  /** Accessible label for the first link row */
  row1AriaLabel?: string;
  /** Accessible label for the second link row */
  row2AriaLabel?: string;
  /** Accessible label for the social media navigation */
  socialAriaLabel?: string;
  /** Social media icon links */
  socialLinks: SocialLink[];
}

const LinkRow: React.FC<{ links: FooterLink[]; ariaLabel?: string }> = ({ links, ariaLabel }) => {
  if (links.length === 0) return null;
  return (
    <nav aria-label={ariaLabel || 'Footer links'} className="poly-page-footer__link-row">
      <ul>
        {links.map((link, i) => (
          <li key={i} className="poly-page-footer__link-item">
            <a href={link.url} className="poly-page-footer__link" {...(link.ctaId ? { 'data-cta-id': link.ctaId } : {})} onClick={() => trackCtaClick(CTA_PREFIXES.footer, { ctaId: link.ctaId, href: link.url, label: link.text })}>
              {link.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export const BottomBar: React.FC<BottomBarProps> = ({
  logoImageUrl, logoAlt, logoUrl, logoCtaId, logoLdAppendix,
  copyrightText, row1Links, row2Links, row1AriaLabel, row2AriaLabel, socialAriaLabel, socialLinks,
}) => {
  const logoContent = logoImageUrl
    ? <Image url={logoImageUrl} alt={logoAlt || (logoUrl ? 'Home' : '')} />
    : null;

  const logoSection = logoImageUrl ? (
    <div className="poly-page-footer__logo">
      {logoUrl ? (
        <a href={logoUrl} {...(logoCtaId ? { 'data-cta-id': logoCtaId } : {})} {...(logoLdAppendix ? { 'data-ld-append': logoLdAppendix } : {})} onClick={() => trackCtaClick(CTA_PREFIXES.footer, { ctaId: logoCtaId, href: logoUrl, label: 'logo' })}>
          {logoContent}
        </a>
      ) : logoContent}
    </div>
  ) : null;

  const hasCenterContent = row1Links.length > 0 || row2Links.length > 0 || copyrightText;

  return (
    <div className="poly-page-footer__bar">
      <div className="poly-page-footer__bar-inner">
        {logoSection}

        {hasCenterContent && (
          <div className="poly-page-footer__center">
            {copyrightText && (
              <span className="poly-page-footer__copyright"><small>{copyrightText}</small></span>
            )}
            <LinkRow links={row1Links} ariaLabel={row1AriaLabel} />
            <LinkRow links={row2Links} ariaLabel={row2AriaLabel} />
          </div>
        )}

        {socialLinks.length > 0 && (
          <nav aria-label={socialAriaLabel || 'Social media'} className="poly-page-footer__social">
            <ul>
              {socialLinks.map((social, i) => (
                <li key={i} className="poly-page-footer__social-item">
                  <a href={social.url} className="poly-page-footer__social-link" target="_blank" rel="noopener noreferrer" aria-label={social.iconAlt || 'Social media link'} {...(social.ctaId ? { 'data-cta-id': social.ctaId } : {})} onClick={() => trackCtaClick(CTA_PREFIXES.footer, { ctaId: social.ctaId, href: social.url, label: social.iconAlt })}>
                    <Image url={social.iconUrl} alt="" />
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>
    </div>
  );
};
